import { useEffect, useState } from 'react';
import { Canvas as FabricCanvas, Rect, Textbox, FabricImage, Circle, Line as FabricLine, FabricObject, Path as FabricPath, FabricText, Group as FabricGroup, Ellipse, ActiveSelection, util as fabricUtil, Point as FabricPoint } from 'fabric';
import { useCanvasStore } from '../stores/canvasStore';
import { useStudioStore } from '../stores/studioStore';
import { useViewportStore } from '../stores/viewportStore';
import { useClipboardStore } from '../stores/clipboardStore';
import { calculateSnap } from '../lib/snapEngine';
import { anchorsToPathData, recalcPathElement, previewPathData } from '../lib/pathUtils';
import { createPenState, penStart, penAddAnchor, penDrag, penMouseUp, penFinalize, penRemoveLastAnchor, isNearFirstAnchor } from '../lib/penToolState';
import type { PenDrawingState } from '../lib/penToolState';
import { createDefaultLine, createDefaultPath, getDashArray } from '../lib/elementDefaults';
import { applyTextboxWidthPolicy, resolveTextIntent, applyTextTransform, getDescenderSafeHeight, ELEMENT_ID_KEY, ARTBOARD_PADDING } from '../lib/textMeasurement';
import { applyVisualsToFabricObject, readVisualsFromFabricObject } from '../lib/fabricVisuals';
import { resolveTextPathRef } from '../lib/fabricFactory';
import { DrawState, updateDrawingPreview, clearDrawingPreview } from '../lib/canvasDrawingPreview';
import { getDefaultTextFont } from '../lib/defaults';
import { loadCustomFont, ensureVariantLoaded } from '../lib/fontLoader';
import type { CanvasElement, TextElement, ShapeElement, ImageElement, ButtonElement, GroupElement, LineElement, PathElement, SvgElement } from '../types';

export function useCanvasEvents(opts: {
  fabricCanvas: FabricCanvas | null;
  artboardRef: React.RefObject<Rect | null>;
  lineDrawRef: React.MutableRefObject<{ startX: number; startY: number; tempLine: FabricLine | null; isDrawing: boolean }>;
  penStateRef: React.MutableRefObject<PenDrawingState>;
  penPreviewPathRef: React.MutableRefObject<FabricPath | null>;
  penPreviewDotsRef: React.MutableRefObject<FabricObject[]>;
  drawStateRef: React.MutableRefObject<DrawState>;
  imageClickPosRef: React.MutableRefObject<{ x: number; y: number } | null>;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
}) {
  const [editingPathId, setEditingPathId] = useState<string | null>(null);
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number } | null>(null);

  const { activeTool } = useCanvasStore();
  const { selectElement, setSelectedElements, clearSelection, updateElement, addElement } = useStudioStore();

  // Helper functions
  function addPenPreviewDot(canvas: FabricCanvas, fabricX: number, fabricY: number) {
    const dot = new Rect({
      left: fabricX - 4,
      top: fabricY - 4,
      width: 8,
      height: 8,
      fill: '#ffffff',
      stroke: '#06b6d4',
      strokeWidth: 1.5,
      selectable: false,
      evented: false,
    });
    (dot as any)._csPenDot = true;
    canvas.add(dot);
    opts.penPreviewDotsRef.current.push(dot);
    canvas.requestRenderAll();
  }

  function cleanupPenPreview() {
    const fc = useCanvasStore.getState().fabricCanvas;
    if (!fc) return;
    if (opts.penPreviewPathRef.current) {
      fc.remove(opts.penPreviewPathRef.current);
      opts.penPreviewPathRef.current = null;
    }
    for (const dot of opts.penPreviewDotsRef.current) {
      fc.remove(dot);
    }
    opts.penPreviewDotsRef.current = [];
    fc.requestRenderAll();
  }

  function finalizePenPath(canvas: FabricCanvas, closed: boolean) {
    const anchors = penFinalize(opts.penStateRef.current, closed);
    cleanupPenPreview();
    useCanvasStore.getState().setIsDrawingPath(false);

    if (!anchors || !opts.artboardRef.current) {
      useCanvasStore.getState().setActiveTool('select');
      return;
    }

    const result = recalcPathElement(anchors, 0, 0, closed);

    const newPath = createDefaultPath(result.anchors, closed, {
      x: result.x,
      y: result.y,
      width: result.width,
      height: result.height,
      pathData: result.pathData,
      zIndex: useStudioStore.getState().canvasState.elements.length,
    });

    useStudioStore.getState().pushUndoState();
    useStudioStore.getState().addElement(newPath);
    useStudioStore.getState().selectElement(newPath.id);
    useCanvasStore.getState().setActiveTool('select');
  }

  // Main event handler useEffect
  useEffect(() => {
    if (!opts.fabricCanvas) return;

    // Read the full active selection, not e.selected (Fabric v6 sends only the newly added delta on selection:updated).
    const syncFabricSelection = () => {
      const canvas = opts.fabricCanvas;
      if (!canvas) return;

      const ids = canvas
        .getActiveObjects()
        .map((obj: any) => obj[ELEMENT_ID_KEY] as string)
        .filter(Boolean);

      const current = useStudioStore.getState().selectedElementIds;
      if (ids.length === current.length && ids.every((id) => current.includes(id))) {
        return;
      }

      if (ids.length === 1) {
        selectElement(ids[0]);
      } else if (ids.length > 1) {
        setSelectedElements(ids);
      }
    };

    const handleSelectionCreated = () => syncFabricSelection();
    const handleSelectionUpdated = () => syncFabricSelection();
    const handleSelectionCleared = () => clearSelection();

    const handleObjectModified = (e: any) => {
      useViewportStore.getState().clearGuides();

      if (!e.target || !opts.artboardRef.current) return;

      // Multi-select (ActiveSelection) resize/move: the ActiveSelection itself
      // has no ELEMENT_ID_KEY. Without this branch the store is never updated
      // when the user resizes the entire artwork via multi-select, causing
      // each element to revert on the next sync.
      //
      // CRITICAL SEQUENCING: Two prior approaches failed:
      //   1. Calling discardActiveObject() synchronously inside object:modified
      //      broke Fabric's mouseup transform cleanup -> stuck selection marquee.
      //   2. Reading world transforms via qrDecompose while children remained
      //      inside the ActiveSelection caused the store->fabric sync to write
      //      world coords into child.left/top, which Fabric interprets as
      //      GROUP-LOCAL coords -> entire artwork visually shifts by the
      //      selection's translation.
      //
      // The correct approach is to defer discard-and-bake to setTimeout(0) so
      // Fabric finishes its own mouseup pipeline first. After discard, each
      // child's left/top/scaleX/scaleY are baked to world coords by Fabric,
      // and the existing single-element per-type logic in handleObjectModified
      // writes correct values to the store.
      if (e.target instanceof ActiveSelection) {
        const children = (e.target as ActiveSelection).getObjects().slice();
        setTimeout(() => {
          if (!opts.fabricCanvas) return;
          opts.fabricCanvas.discardActiveObject();
          for (const child of children) {
            child.setCoords();
            handleObjectModified({ target: child });
          }
          opts.fabricCanvas.requestRenderAll();
        }, 0);
        return;
      }

      const elementId = (e.target as any)[ELEMENT_ID_KEY] as string;
      if (!elementId) return;

      const artboardLeft = opts.artboardRef.current.left ?? ARTBOARD_PADDING;
      const artboardTop = opts.artboardRef.current.top ?? ARTBOARD_PADDING;

      const obj = e.target as FabricObject;

      const storeGroupEl = useStudioStore.getState().canvasState.elements.find((el) => el.id === elementId);
      if (storeGroupEl?.type === 'group') {
        const groupEl = storeGroupEl as GroupElement;
        const scX = obj.scaleX ?? 1;
        const scY = obj.scaleY ?? 1;
        const newGroupX = Math.round((obj.left ?? 0) - artboardLeft);
        const newGroupY = Math.round((obj.top ?? 0) - artboardTop);
        const newGroupW = Math.max(1, Math.round(groupEl.width * scX));
        const newGroupH = Math.max(1, Math.round(groupEl.height * scY));
        const rotation = Math.round(obj.angle ?? 0);

        obj.set({ scaleX: 1, scaleY: 1, width: newGroupW, height: newGroupH });
        obj.setCoords();

        const allEls = useStudioStore.getState().canvasState.elements;
        const updates: Array<{ id: string; changes: Partial<CanvasElement> }> = [{
          id: elementId,
          changes: {
            x: newGroupX,
            y: newGroupY,
            width: newGroupW,
            height: newGroupH,
            rotation,
          } as Partial<CanvasElement>,
        }];

        for (const childId of groupEl.childIds) {
          const child = allEls.find((el) => el.id === childId);
          if (!child) continue;
          const relX = child.x - groupEl.x;
          const relY = child.y - groupEl.y;
          const nx = Math.round(newGroupX + relX * scX);
          const ny = Math.round(newGroupY + relY * scY);
          const childChanges: Partial<CanvasElement> = { x: nx, y: ny } as Partial<CanvasElement>;
          if (Math.abs(scX - 1) > 0.0001 || Math.abs(scY - 1) > 0.0001) {
            (childChanges as any).width = Math.max(1, Math.round(child.width * scX));
            (childChanges as any).height = Math.max(1, Math.round(child.height * scY));
            if (child.type === 'text' || child.type === 'button') {
              const currentFS = (child as any).fontSize ?? 16;
              (childChanges as any).fontSize = Math.max(1, Math.round(currentFS * Math.max(scX, scY)));
            }
          }
          updates.push({ id: childId, changes: childChanges });
        }

        useStudioStore.getState().updateElements(updates);
        return;
      }

      if (obj instanceof FabricPath) {
        const storeEl = useStudioStore.getState().canvasState.elements.find((el) => el.id === elementId);
        if (storeEl?.type === 'path') {
          const pathEl = storeEl as PathElement;
          const scX = obj.scaleX ?? 1;
          const scY = obj.scaleY ?? 1;
          const newW = Math.round((obj.width ?? 1) * scX);
          const newH = Math.round((obj.height ?? 1) * scY);
          obj.set({ scaleX: 1, scaleY: 1, width: newW, height: newH });
          obj.setCoords();
          updateElement(elementId, {
            x: Math.round((obj.left ?? 0) - artboardLeft),
            y: Math.round((obj.top ?? 0) - artboardTop),
            width: newW,
            height: newH,
            rotation: Math.round(obj.angle ?? 0),
          } as Partial<CanvasElement>);
          return;
        }
      }

      if (obj instanceof FabricLine) {
        const storeEl = useStudioStore.getState().canvasState.elements.find((el) => el.id === elementId);
        if (storeEl?.type === 'line') {
          const fl = obj as FabricLine;
          const lx1 = fl.x1 ?? 0;
          const ly1 = fl.y1 ?? 0;
          const lx2 = fl.x2 ?? 0;
          const ly2 = fl.y2 ?? 0;
          const cx = (lx1 + lx2) / 2;
          const cy = (ly1 + ly2) / 2;
          const m = fl.calcTransformMatrix();
          const wp1 = fabricUtil.transformPoint(new FabricPoint(lx1 - cx, ly1 - cy), m);
          const wp2 = fabricUtil.transformPoint(new FabricPoint(lx2 - cx, ly2 - cy), m);
          fl.set({
            x1: wp1.x,
            y1: wp1.y,
            x2: wp2.x,
            y2: wp2.y,
            angle: 0,
            scaleX: 1,
            scaleY: 1,
          });
          fl.setCoords();
          const x1 = wp1.x - artboardLeft;
          const y1 = wp1.y - artboardTop;
          const x2 = wp2.x - artboardLeft;
          const y2 = wp2.y - artboardTop;
          const minX = Math.min(x1, x2);
          const minY = Math.min(y1, y2);
          const maxX = Math.max(x1, x2);
          const maxY = Math.max(y1, y2);
          updateElement(elementId, {
            x: minX,
            y: minY,
            width: Math.max(1, maxX - minX),
            height: Math.max(1, maxY - minY),
            x1: x1 - minX,
            y1: y1 - minY,
            x2: x2 - minX,
            y2: y2 - minY,
          } as Partial<CanvasElement>);
          return;
        }
      }

      if ((obj as any)._csIsSvg) {
        const storeEl = useStudioStore.getState().canvasState.elements.find((el) => el.id === elementId);
        if (storeEl?.type === 'svg') {
          const svgEl = storeEl as SvgElement;
          const scX = obj.scaleX ?? 1;
          const scY = obj.scaleY ?? 1;

          // CONTRACT: all sync writers (fabricFactory, fabricSync, useCanvasSync)
          // keep obj.width at the SVG intrinsic originalWidth and maintain a
          // STANDING scaleX = svgEl.width / originalWidth. Therefore obj.scaleX
          // here is the TOTAL display scale (standing × any drag delta), NOT a
          // fresh delta. A pure move leaves scaleX at the standing value.
          const expectedScaleX = svgEl.width / (svgEl.originalWidth || 1);
          const expectedScaleY = svgEl.height / (svgEl.originalHeight || 1);
          const wasScaled =
            Math.abs(scX - expectedScaleX) > 0.001 ||
            Math.abs(scY - expectedScaleY) > 0.001;

          if (!wasScaled) {
            // Pure move (or rotation): persist position only. Do NOT touch
            // width/height — re-deriving them from the standing scale was the
            // multiplicative shrink loop.
            console.log('[SVG-RESIZE-EVENT]', {
              stage: 'handleObjectModified-move-only',
              elementId,
              fabric_scaleX: scX,
              fabric_scaleY: scY,
              expected_scaleX: expectedScaleX,
              expected_scaleY: expectedScaleY,
              store_width_unchanged: svgEl.width,
              store_height_unchanged: svgEl.height,
            });
            updateElement(elementId, {
              x: Math.round((obj.left ?? 0) - artboardLeft),
              y: Math.round((obj.top ?? 0) - artboardTop),
              rotation: Math.round(obj.angle ?? 0),
            } as Partial<CanvasElement>);
            return;
          }

          // Scale gesture: obj.scaleX is the new TOTAL display scale. Bake it
          // from the immutable intrinsic dimensions.
          // formula: newW = svgEl.originalWidth * scX  (now the code matches)
          const newW = Math.round((svgEl.originalWidth || 1) * scX);
          const newH = Math.round((svgEl.originalHeight || 1) * scY);
          const newScaleX = newW / (svgEl.originalWidth || 1);
          const newScaleY = newH / (svgEl.originalHeight || 1);

          console.log('[SVG-RESIZE-EVENT]', {
            stage: 'handleObjectModified-entry',
            elementId,
            fabric_scaleX_in: obj.scaleX,
            fabric_scaleY_in: obj.scaleY,
            fabric_width_in: obj.width,
            fabric_height_in: obj.height,
            fabric_left_in: obj.left,
            fabric_top_in: obj.top,
            store_width_in: svgEl.width,
            store_height_in: svgEl.height,
            store_originalWidth: svgEl.originalWidth,
            store_originalHeight: svgEl.originalHeight,
            computed_newW: newW,
            computed_newH: newH,
            computed_newScaleX: newScaleX,
            computed_newScaleY: newScaleY,
            formula: 'newW = svgEl.originalWidth * scX (total standing scale baked from intrinsic)',
          });

          obj.set({ scaleX: newScaleX, scaleY: newScaleY });
          obj.setCoords();

          console.log('[SVG-RESIZE-EVENT]', {
            stage: 'handleObjectModified-after-fabric-set',
            elementId,
            fabric_scaleX_after: obj.scaleX,
            fabric_scaleY_after: obj.scaleY,
            fabric_width_after: obj.width,
            fabric_height_after: obj.height,
          });

          updateElement(elementId, {
            x: Math.round((obj.left ?? 0) - artboardLeft),
            y: Math.round((obj.top ?? 0) - artboardTop),
            width: newW,
            height: newH,
            rotation: Math.round(obj.angle ?? 0),
          } as Partial<CanvasElement>);

          console.log('[SVG-RESIZE-EVENT]', {
            stage: 'handleObjectModified-store-write',
            elementId,
            written_width: newW,
            written_height: newH,
            written_x: Math.round((obj.left ?? 0) - artboardLeft),
            written_y: Math.round((obj.top ?? 0) - artboardTop),
          });

          return;
        }
      }

      if (obj instanceof FabricText && !(obj instanceof Textbox) && (obj as any).path) {
        const scX = obj.scaleX ?? 1;
        const scY = obj.scaleY ?? 1;
        const scaleFactor = Math.max(scX, scY);
        const currentFontSize = (obj as any).fontSize ?? 16;
        const newFontSize = Math.round(currentFontSize * scaleFactor);
        obj.set({ fontSize: newFontSize, scaleX: 1, scaleY: 1 });
        (obj as any).initDimensions();
        obj.setCoords();
        const visuals = readVisualsFromFabricObject(obj);
        updateElement(elementId, {
          x: Math.round((obj.left ?? 0) - artboardLeft),
          y: Math.round((obj.top ?? 0) - artboardTop),
          width: Math.round(obj.width ?? 0),
          height: Math.round(obj.height ?? 0),
          rotation: Math.round(obj.angle ?? 0),
          fontSize: newFontSize,
          ...visuals,
        } as Partial<CanvasElement>);
        obj.canvas?.renderAll();
        return;
      }

      const scaleX = obj.scaleX ?? 1;
      const scaleY = obj.scaleY ?? 1;
      const isTextObject = obj instanceof Textbox;

      const visuals = readVisualsFromFabricObject(obj);

      /**
       * ANTI-REGRESSION WARNING -- TEXT WIDTH BUG
       * ==========================================
       * This branch has SEPARATE logic for corner drag vs side-handle drag.
       *
       * Corner drag (scaleX/scaleY != 1): multiply fontSize by scaleFactor,
       *   set new width, reset scaleX/scaleY to 1.
       * Side-handle drag (scaleX/scaleY == 1): width already changed by Fabric.
       *
       * ALWAYS call initDimensions() + setCoords() after fontSize changes.
       * ALWAYS clamp width via clampTextboxWidth() to prevent blank space.
       *
       * DO NOT merge text and non-text handling into a single code path.
       * DO NOT remove the width clamping. This bug has regressed 3 times.
       */
      if (isTextObject) {
        const textObj = obj as Textbox;
        const currentFontSize = textObj.fontSize ?? 16;
        const wasScaled = Math.abs(scaleX - 1) > 0.001 || Math.abs(scaleY - 1) > 0.001;

        // Phase B.4: CWT elements have designer-fixed dimensions. Per CWT/CWS
        // Spec Lock §4, width/height/widthIntent MUST NOT be modified by
        // object:modified except for explicit drag-resize (wasScaled).
        const storeElement = useStudioStore
          .getState()
          .canvasState.elements.find((e) => e.id === elementId) as TextElement | undefined;
        const isCWT = storeElement?.textFitMode === 'contain-within-textbox' ||
                      storeElement?.textFitMode === 'contain-within-structure';

        if (isCWT && !wasScaled) {
          updateElement(elementId, {
            x: (obj.left ?? 0) - artboardLeft,
            y: (obj.top ?? 0) - artboardTop,
            rotation: obj.angle ?? 0,
            ...visuals,
          } as Partial<CanvasElement>);
          obj.canvas?.renderAll();
          return;
        }

        let newFontSize = currentFontSize;
        let newWidth = obj.width ?? 100;

        if (wasScaled) {
          const scaleFactor = Math.max(scaleX, scaleY);
          newFontSize = Math.round(currentFontSize * scaleFactor);
          newWidth = (obj.width ?? 100) * scaleX;

          textObj.set({
            fontSize: newFontSize,
            width: newWidth,
            scaleX: 1,
            scaleY: 1,
          });
          textObj.initDimensions();
          textObj.setCoords();
        }

        if (!isCWT) {
          applyTextboxWidthPolicy(textObj, 'manual');
        }
        newWidth = textObj.width;

        updateElement(elementId, {
          x: (obj.left ?? 0) - artboardLeft,
          y: (obj.top ?? 0) - artboardTop,
          width: newWidth,
          height: textObj.height ?? 0,
          rotation: obj.angle ?? 0,
          fontSize: newFontSize,
          ...(isCWT ? {} : { widthIntent: 'manual' as const }),
          ...visuals,
        } as Partial<CanvasElement>);
        obj.canvas?.renderAll();
        return;
      } else if ((obj as any)._csIsButton) {
        const bg = (obj as any)._csButtonBg as Rect | undefined;
        const txt = (obj as any)._csButtonText as FabricText | undefined;
        const newWidth = Math.round((obj.width ?? 180) * scaleX);
        const newHeight = Math.round((obj.height ?? 48) * scaleY);

        if (bg) {
          bg.set({ width: newWidth, height: newHeight });
        }
        if (txt && (Math.abs(scaleX - 1) > 0.001 || Math.abs(scaleY - 1) > 0.001)) {
          const sf = Math.max(scaleX, scaleY);
          txt.set({ fontSize: Math.round((txt.fontSize || 16) * sf) });
        }
        obj.set({ scaleX: 1, scaleY: 1 });
        obj.setCoords();

        const btnUpdates: Partial<CanvasElement> = {
          // Phase F.2 fix: FabricGroup has originX/originY:'center', so obj.left/obj.top
          // represent the visual CENTER of the button. Translate to top-left for canonical
          // storage (Decision [12]: element.x means top-left for all types).
          x: Math.round((obj.left ?? 0) - artboardLeft - (newWidth / 2)),
          y: Math.round((obj.top ?? 0) - artboardTop - (newHeight / 2)),
          width: newWidth,
          height: newHeight,
          rotation: Math.round(obj.angle ?? 0),
          fontSize: txt?.fontSize,
          ...visuals,
        };
        console.log('[Phase F.1.2] handleObjectModified button write', { elementId, btnUpdates });
        updateElement(elementId, btnUpdates);
      } else {
        const normalizedW = Math.round((obj.width ?? 0) * scaleX);
        const normalizedH = Math.round((obj.height ?? 0) * scaleY);
        const isImg = obj instanceof FabricImage;

        if (isImg) {
          obj.set({ scaleX: 1, scaleY: 1 });
        } else {
          obj.set({ scaleX: 1, scaleY: 1, width: normalizedW, height: normalizedH });
        }
        obj.setCoords();

        const updates: Partial<CanvasElement> = {
          x: Math.round((obj.left ?? 0) - artboardLeft),
          y: Math.round((obj.top ?? 0) - artboardTop),
          width: normalizedW,
          height: normalizedH,
          rotation: Math.round(obj.angle ?? 0),
          ...visuals,
        };

        updateElement(elementId, updates);
      }

      obj.canvas?.renderAll();
    };

    const handleObjectMoving = (e: any) => {
      if (!e.target || !opts.artboardRef.current) return;

      const artboardLeft = opts.artboardRef.current.left ?? ARTBOARD_PADDING;
      const artboardTop = opts.artboardRef.current.top ?? ARTBOARD_PADDING;

      const vpState = useViewportStore.getState();
      const elementId = (e.target as any)[ELEMENT_ID_KEY] as string;
      if (!elementId) return;

      const movingStoreEl = useStudioStore.getState().canvasState.elements.find((el) => el.id === elementId);
      if (movingStoreEl?.type === 'group') {
        const groupEl = movingStoreEl as GroupElement;
        const obj = e.target as FabricObject;
        const deltaX = (obj.left ?? 0) - (artboardLeft + groupEl.x);
        const deltaY = (obj.top ?? 0) - (artboardTop + groupEl.y);
        const allEls = useStudioStore.getState().canvasState.elements;
        for (const childId of groupEl.childIds) {
          const child = allEls.find((el) => el.id === childId);
          if (!child) continue;
          const childObj = opts.fabricCanvas.getObjects().find(
            (o) => (o as any)[ELEMENT_ID_KEY] === childId
          );
          if (!childObj) continue;
          if (childObj instanceof FabricLine && child.type === 'line') {
            // FabricLine renders from its internal x1/y1/x2/y2 coords. Setting
            // `left`/`top` alone can drift visually vs. other fabric objects
            // during group drags, so shift the endpoints explicitly.
            const lineEl = child as LineElement;
            childObj.set({
              x1: artboardLeft + lineEl.x + lineEl.x1 + deltaX,
              y1: artboardTop + lineEl.y + lineEl.y1 + deltaY,
              x2: artboardLeft + lineEl.x + lineEl.x2 + deltaX,
              y2: artboardTop + lineEl.y + lineEl.y2 + deltaY,
            });
          } else {
            childObj.set({
              left: artboardLeft + child.x + deltaX,
              top: artboardTop + child.y + deltaY,
            });
          }
          childObj.setCoords();
        }
        vpState.clearGuides();
        opts.fabricCanvas.requestRenderAll();
        return;
      }

      const hasAnySnap = vpState.snapEnabled || vpState.smartGuidesEnabled || (vpState.showGuides && vpState.snapToGuides);
      if (!hasAnySnap) {
        vpState.clearGuides();
        return;
      }

      const obj = e.target as FabricObject;
      const bRect = typeof (obj as any).getBoundingRect === 'function'
        ? (obj as any).getBoundingRect(true, true)
        : { left: obj.left ?? 0, top: obj.top ?? 0, width: (obj.width ?? 0) * (obj.scaleX ?? 1), height: (obj.height ?? 0) * (obj.scaleY ?? 1) };
      const relX = bRect.left - artboardLeft;
      const relY = bRect.top - artboardTop;
      const objW = bRect.width;
      const objH = bRect.height;
      const offsetX = (obj.left ?? 0) - bRect.left;
      const offsetY = (obj.top ?? 0) - bRect.top;

      const cw = useCanvasStore.getState().canvasWidth;
      const ch = useCanvasStore.getState().canvasHeight;
      const allElements = useStudioStore.getState().canvasState.elements;
      const others = allElements
        .filter((el) => el.id !== elementId)
        .map((el) => ({ id: el.id, x: el.x, y: el.y, width: el.width, height: el.height, source: 'element' as const }));

      const result = calculateSnap(
        { x: relX, y: relY, width: objW, height: objH },
        others,
        vpState.gridSize,
        vpState.snapEnabled,
        vpState.smartGuidesEnabled,
        { width: cw, height: ch }
      );

      if (vpState.showGuides && vpState.snapToGuides && vpState.guides.length > 0) {
        const GUIDE_THRESHOLD = 5;
        const movLeft = result.x !== null ? result.x : relX;
        const movTop = result.y !== null ? result.y : relY;
        const movRight = movLeft + objW;
        const movBottom = movTop + objH;
        const movCenterX = movLeft + objW / 2;
        const movCenterY = movTop + objH / 2;

        for (const guide of vpState.guides) {
          if (guide.orientation === 'vertical') {
            const candidates = [
              { edge: movLeft, offset: 0 },
              { edge: movRight, offset: -objW },
              { edge: movCenterX, offset: -objW / 2 },
            ];
            for (const c of candidates) {
              const dist = Math.abs(c.edge - guide.position);
              if (dist < GUIDE_THRESHOLD) {
                result.x = guide.position + c.offset;
                result.guides = result.guides.filter((g) => g.orientation !== 'vertical');
                result.guides.push({ orientation: 'vertical', position: guide.position, type: 'edge' });
                break;
              }
            }
          } else {
            const candidates = [
              { edge: movTop, offset: 0 },
              { edge: movBottom, offset: -objH },
              { edge: movCenterY, offset: -objH / 2 },
            ];
            for (const c of candidates) {
              const dist = Math.abs(c.edge - guide.position);
              if (dist < GUIDE_THRESHOLD) {
                result.y = guide.position + c.offset;
                result.guides = result.guides.filter((g) => g.orientation !== 'horizontal');
                result.guides.push({ orientation: 'horizontal', position: guide.position, type: 'edge' });
                break;
              }
            }
          }
        }
      }

      if (result.x !== null) {
        obj.set('left', artboardLeft + result.x + offsetX);
      }
      if (result.y !== null) {
        obj.set('top', artboardTop + result.y + offsetY);
      }

      if (result.guides.length > 0) {
        const offsetGuides = result.guides.map((g) => ({
          ...g,
          position: g.position + (g.orientation === 'vertical' ? artboardLeft : artboardTop),
        }));
        useViewportStore.getState().setActiveGuides(offsetGuides);
      } else {
        useViewportStore.getState().clearGuides();
      }
    };

    const handleMouseDown = (opt: any) => {
      const tool = useCanvasStore.getState().activeTool;
      if (tool === 'select' || tool === 'pan' || tool === 'zoom') return;
      if (!opt.pointer || !opts.artboardRef.current) return;

      if (editingPathId) {
        const target = opt.target;
        const targetId = target ? (target as any)[ELEMENT_ID_KEY] : null;
        if (targetId !== editingPathId) {
          setEditingPathId(null);
        }
      }

      const artboardLeft = opts.artboardRef.current.left ?? ARTBOARD_PADDING;
      const artboardTop = opts.artboardRef.current.top ?? ARTBOARD_PADDING;
      // Force VPT-inverse coordinate conversion. opt.pointer is unreliable across
      // Fabric versions with custom viewportTransform; the explicit getPointer
      // call always applies the inverse VPT.
      const pointer = opts.fabricCanvas.getPointer(opt.e);
      const cw = useCanvasStore.getState().canvasWidth;
      const ch = useCanvasStore.getState().canvasHeight;
      const artboardRight = artboardLeft + cw;
      const artboardBottom = artboardTop + ch;

      if (
        pointer.x < artboardLeft ||
        pointer.x > artboardRight ||
        pointer.y < artboardTop ||
        pointer.y > artboardBottom
      ) return;

      const relX = Math.round(pointer.x - artboardLeft);
      const relY = Math.round(pointer.y - artboardTop);
      const currentElements = useStudioStore.getState().canvasState.elements;

      if (tool === 'eyedropper') {
        const fc = useCanvasStore.getState().fabricCanvas;
        if (!fc) return;
        const canvasEl = (fc as any).lowerCanvasEl as HTMLCanvasElement | undefined;
        if (!canvasEl) return;
        const ctx = canvasEl.getContext('2d');
        if (!ctx) return;

        const scaleX = canvasEl.width / canvasEl.clientWidth;
        const scaleY = canvasEl.height / canvasEl.clientHeight;
        const pixelX = Math.round(pointer.x * scaleX);
        const pixelY = Math.round(pointer.y * scaleY);
        const pixel = ctx.getImageData(pixelX, pixelY, 1, 1).data;
        const hex = `#${pixel[0].toString(16).padStart(2, '0')}${pixel[1].toString(16).padStart(2, '0')}${pixel[2].toString(16).padStart(2, '0')}`;

        useCanvasStore.getState().setLastSampledColor(hex);

        const selectedId = useStudioStore.getState().selectedElementId;
        if (selectedId) {
          const el = useStudioStore.getState().canvasState.elements.find((e) => e.id === selectedId);
          if (el) {
            const isShift = (opt.e as MouseEvent).shiftKey;
            if (isShift) {
              if (el.type === 'text' || el.type === 'shape' || el.type === 'line' || el.type === 'path' || el.type === 'button') {
                useStudioStore.getState().updateElement(selectedId, { stroke: hex, strokeColor: hex } as Partial<CanvasElement>);
              }
            } else {
              if (el.type === 'text') {
                useStudioStore.getState().updateElement(selectedId, { color: hex } as Partial<CanvasElement>);
              } else if (el.type === 'shape' || el.type === 'button') {
                useStudioStore.getState().updateElement(selectedId, {
                  fill: { type: 'solid', color: hex },
                } as Partial<CanvasElement>);
              }
            }
          }
        }

        useCanvasStore.getState().setActiveTool('select');
        return;
      }

      if (tool === 'pen') {
        const penState = opts.penStateRef.current;
        (opt.e as MouseEvent).preventDefault();
        const fc = useCanvasStore.getState().fabricCanvas;

        if (!penState.isActive) {
          penStart(penState, relX, relY);
          useCanvasStore.getState().setIsDrawingPath(true);
          if (fc) addPenPreviewDot(fc, pointer.x, pointer.y);
        } else {
          if (isNearFirstAnchor(penState, relX, relY)) {
            if (fc) finalizePenPath(fc, true);
          } else {
            penAddAnchor(penState, relX, relY);
            if (fc) addPenPreviewDot(fc, pointer.x, pointer.y);
          }
        }
        return;
      }

      if (tool === 'line') {
        const tempLine = new FabricLine(
          [pointer.x, pointer.y, pointer.x, pointer.y],
          {
            stroke: '#06b6d4',
            strokeWidth: 2,
            selectable: false,
            evented: false,
            strokeDashArray: [6, 4],
          }
        );
        opts.fabricCanvas.add(tempLine);
        opts.lineDrawRef.current = {
          startX: relX,
          startY: relY,
          tempLine,
          isDrawing: true,
        };
        (opt.e as MouseEvent).preventDefault();
        return;
      }

      if (tool === 'text') {
        const defaultFont = getDefaultTextFont();
        if (!defaultFont) {
          console.warn('[Text Tool] No custom fonts available in cs_fonts. Upload a font in Font Manager before creating text elements.');
          useCanvasStore.getState().setActiveTool('select');
          return;
        }

        const newElement: TextElement = {
          id: crypto.randomUUID(),
          type: 'text',
          name: 'New Text',
          x: relX,
          y: relY,
          width: 200,
          height: 40,
          rotation: 0,
          visible: true,
          locked: false,
          zIndex: currentElements.length,
          opacity: 1,
          layer: 'OVERLAY_LAYER',
          contentType: 'STATIC',
          text: 'New Text',
          fontFamily: defaultFont.fontFamily,
          fontVariantId: defaultFont.fontVariantId,
          fontSize: 24,
          fontWeight: defaultFont.fontWeight,
          fontStyle: defaultFont.fontStyle,
          textDecoration: 'none',
          textAlign: 'left',
          lineHeight: 1.2,
          letterSpacing: 0,
          textTransform: 'none',
          color: '#ffffff',
          widthIntent: 'auto',
        };

        loadCustomFont(defaultFont.fontFamily)
          .then(() => ensureVariantLoaded(
            defaultFont.fontFamily,
            defaultFont.fontWeight,
            defaultFont.fontStyle
          ))
          .then(() => {
            addElement(newElement);
            useCanvasStore.getState().setActiveTool('select');

            requestAnimationFrame(() => {
              const fc = useCanvasStore.getState().fabricCanvas;
              if (!fc) return;
              const fabricObj = fc.getObjects().find(
                (o: any) => o[ELEMENT_ID_KEY] === newElement.id
              );
              if (fabricObj && fabricObj instanceof Textbox) {
                fc.setActiveObject(fabricObj);
                fabricObj.enterEditing();
                fabricObj.selectAll();
                fc.requestRenderAll();
              }
            });
          })
          .catch((err) => {
            console.error('[Text Tool] Font load failed:', err);
            useCanvasStore.getState().setActiveTool('select');
          });
      } else if (tool === 'shape') {
        const pendingType = useCanvasStore.getState().pendingShapeType || 'rectangle';
        opts.drawStateRef.current = {
          isDrawing: true,
          tool: 'shape',
          shapeType: pendingType,
          startX: relX,
          startY: relY,
          currentX: relX,
          currentY: relY,
        };
        (opt.e as MouseEvent).preventDefault();
      } else if (tool === 'button') {
        opts.drawStateRef.current = {
          isDrawing: true,
          tool: 'button',
          startX: relX,
          startY: relY,
          currentX: relX,
          currentY: relY,
        };
        (opt.e as MouseEvent).preventDefault();
      } else if (tool === 'image') {
        opts.imageClickPosRef.current = { x: relX, y: relY };
        opts.fileInputRef.current?.click();
        useCanvasStore.getState().setActiveTool('select');
      }
    };

    const handleTextChanged = (e: any) => {
      const obj = e.target;
      if (!obj) return;
      const elementId = (obj as any)[ELEMENT_ID_KEY] as string;
      if (!elementId) return;
      const newText = (obj as Textbox).text || '';
      useStudioStore.getState().updateElement(elementId, { text: newText } as Partial<CanvasElement>);
    };

    const handleTextEditingEntered = (e: any) => {
      const obj = e.target;
      if (!obj) return;
      const elementId = (obj as any)[ELEMENT_ID_KEY] as string;
      if (!elementId) return;
      const storeEl = useStudioStore
        .getState()
        .canvasState.elements.find((el) => el.id === elementId) as TextElement | undefined;
      if (!storeEl) return;
      // Enter edit mode on the raw text, not the transformed display text,
      // so uppercase/lowercase/capitalize doesn't get baked in when the user
      // types.
      if (storeEl.textTransform && storeEl.textTransform !== 'none') {
        (obj as Textbox).set({ text: storeEl.text || '' });
        (obj as Textbox).initDimensions();
        obj.canvas?.requestRenderAll();
      }
    };

    const handleTextEditingExited = (e: any) => {
      const obj = e.target;
      if (!obj || !opts.artboardRef.current) return;
      const elementId = (obj as any)[ELEMENT_ID_KEY] as string;
      if (!elementId) return;
      const textObj = obj as Textbox;
      const storeEl = useStudioStore
        .getState()
        .canvasState.elements.find((el) => el.id === elementId) as TextElement | undefined;
      // Phase B.5: CWT (Contain Within Textbox) elements have designer-fixed
      // dimensions per CWT/CWS Spec Lock §4. On inline-editing exit, persist
      // only the text content (the user's edit) and position. NEVER modify
      // width/height — applyTextboxWidthPolicy would floor to advance-width
      // and corrupt the designer's box.
      const isCWT = storeEl?.textFitMode === 'contain-within-textbox' ||
                    storeEl?.textFitMode === 'contain-within-structure';
      const intent = resolveTextIntent(storeEl);
      const rawText = textObj.text || '';
      if (storeEl?.textTransform && storeEl.textTransform !== 'none') {
        textObj.set({ text: applyTextTransform(rawText, storeEl.textTransform) });
        textObj.initDimensions();
      }
      const artboardLeft = opts.artboardRef.current.left ?? ARTBOARD_PADDING;
      const artboardTop = opts.artboardRef.current.top ?? ARTBOARD_PADDING;

      if (isCWT) {
        // CWT: persist text + position only. Width/height are designer-locked.
        useStudioStore.getState().updateElement(elementId, {
          text: rawText,
          x: (textObj.left ?? 0) - artboardLeft,
          y: (textObj.top ?? 0) - artboardTop,
        } as Partial<CanvasElement>);
        return;
      }

      applyTextboxWidthPolicy(textObj, intent);
      useStudioStore.getState().updateElement(elementId, {
        text: rawText,
        width: textObj.width ?? 0,
        height: textObj.height ?? 0,
        x: (textObj.left ?? 0) - artboardLeft,
        y: (textObj.top ?? 0) - artboardTop,
      } as Partial<CanvasElement>);
    };

    opts.fabricCanvas.on('selection:created', handleSelectionCreated);
    opts.fabricCanvas.on('selection:updated', handleSelectionUpdated);
    opts.fabricCanvas.on('selection:cleared', handleSelectionCleared);
    opts.fabricCanvas.on('object:modified', handleObjectModified);
    opts.fabricCanvas.on('object:moving', handleObjectMoving);
    opts.fabricCanvas.on('mouse:down', handleMouseDown);
    opts.fabricCanvas.on('text:changed', handleTextChanged);
    opts.fabricCanvas.on('text:editing:entered', handleTextEditingEntered);
    opts.fabricCanvas.on('text:editing:exited', handleTextEditingExited);

    const handleDblClick = (e: any) => {
      if (!e.target) return;
      const elementId = (e.target as any)[ELEMENT_ID_KEY] as string;
      if (!elementId) return;
      const el = useStudioStore.getState().canvasState.elements.find((el) => el.id === elementId);
      if (el?.type === 'path') {
        setEditingPathId(elementId);
        opts.fabricCanvas.discardActiveObject();
        opts.fabricCanvas.requestRenderAll();
      }
    };
    opts.fabricCanvas.on('mouse:dblclick', handleDblClick);

    return () => {
      opts.fabricCanvas.off('selection:created', handleSelectionCreated);
      opts.fabricCanvas.off('selection:updated', handleSelectionUpdated);
      opts.fabricCanvas.off('selection:cleared', handleSelectionCleared);
      opts.fabricCanvas.off('object:modified', handleObjectModified);
      opts.fabricCanvas.off('object:moving', handleObjectMoving);
      opts.fabricCanvas.off('mouse:down', handleMouseDown);
      opts.fabricCanvas.off('text:changed', handleTextChanged);
      opts.fabricCanvas.off('text:editing:entered', handleTextEditingEntered);
      opts.fabricCanvas.off('text:editing:exited', handleTextEditingExited);
      opts.fabricCanvas.off('mouse:dblclick', handleDblClick);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opts.fabricCanvas]);

  // Right-click context menu useEffect
  useEffect(() => {
    if (!opts.fabricCanvas) return;

    const handleRightClick = (opt: any) => {
      const e = opt.e as MouseEvent;
      if (e.button !== 2) return;

      e.preventDefault();
      e.stopPropagation();

      const target = opt.target as FabricObject | null;
      if (target && !(target as any)._csIsArtboard && !(target as any)._csIsGrid && !(target as any)._csIsArtboardBorder) {
        const targetId = (target as any)[ELEMENT_ID_KEY] as string;
        if (targetId) {
          const currentIds = useStudioStore.getState().selectedElementIds;
          if (!currentIds.includes(targetId)) {
            if (e.shiftKey) {
              useStudioStore.getState().addToSelection(targetId);
            } else {
              useStudioStore.getState().selectElement(targetId);
              opts.fabricCanvas.setActiveObject(target);
            }
          }
        }
        setContextMenu({ x: e.clientX, y: e.clientY });
      } else {
        const hasClip = useClipboardStore.getState().hasContent();
        if (hasClip) {
          useStudioStore.getState().clearSelection();
          setContextMenu({ x: e.clientX, y: e.clientY });
        } else {
          setContextMenu(null);
        }
      }
    };

    opts.fabricCanvas.on('mouse:down', handleRightClick);

    return () => {
      opts.fabricCanvas.off('mouse:down', handleRightClick);
    };
  }, [opts.fabricCanvas]);

  // activeTool cleanup useEffect
  useEffect(() => {
    if (activeTool !== 'pen' && opts.penStateRef.current.isActive) {
      opts.penStateRef.current.isActive = false;
      opts.penStateRef.current.anchors = [];
      opts.penStateRef.current.isDragging = false;
      opts.penStateRef.current.dragAnchorIndex = -1;
      cleanupPenPreview();
      useCanvasStore.getState().setIsDrawingPath(false);
    }
    if (opts.drawStateRef.current.isDrawing) {
      opts.drawStateRef.current.isDrawing = false;
      clearDrawingPreview(useCanvasStore.getState().fabricCanvas);
    }
    if (activeTool !== 'select') {
      setEditingPathId(null);
    }
  }, [activeTool]);

  // Pen key handler useEffect
  useEffect(() => {
    const isDrawing = useCanvasStore.getState().isDrawingPath;
    if (!isDrawing) return;

    const handlePenKeyDown = (e: KeyboardEvent) => {
      if (!opts.penStateRef.current.isActive) return;

      if (e.key === 'Escape') {
        e.preventDefault();
        opts.penStateRef.current.isActive = false;
        opts.penStateRef.current.anchors = [];
        opts.penStateRef.current.isDragging = false;
        cleanupPenPreview();
        useCanvasStore.getState().setIsDrawingPath(false);
        useCanvasStore.getState().setActiveTool('select');
        return;
      }

      if (e.key === 'Enter') {
        e.preventDefault();
        const fc = useCanvasStore.getState().fabricCanvas;
        if (fc) finalizePenPath(fc, false);
        return;
      }

      if (e.key === 'Backspace' || e.key === 'Delete') {
        e.preventDefault();
        penRemoveLastAnchor(opts.penStateRef.current);
        const dots = opts.penPreviewDotsRef.current;
        const fc = useCanvasStore.getState().fabricCanvas;
        if (dots.length > 1 && fc) {
          const removed = dots.pop();
          if (removed) fc.remove(removed);
          fc.requestRenderAll();
        }
        return;
      }
    };

    window.addEventListener('keydown', handlePenKeyDown);
    return () => window.removeEventListener('keydown', handlePenKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useCanvasStore.getState().isDrawingPath]);

  return { editingPathId, setEditingPathId, contextMenu, setContextMenu };
}